import { useCallback, useEffect, useRef, useState } from 'react';

type UseWritingAnimationOptions = {
  charsPerTick?: number;
  intervalMs?: number;
  enabled?: boolean;
};

const useWritingAnimation = (text: string | null, { charsPerTick = 2, intervalMs = 20, enabled = true }: UseWritingAnimationOptions = {}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const completedTextRef = useRef<string | null>(null);

  useEffect(() => {
    if (text == null) {
      setDisplayedText('');
      setIsAnimating(false);
      return undefined;
    }

    if (!enabled || completedTextRef.current === text) {
      setDisplayedText(text);
      setIsAnimating(false);
      return undefined;
    }

    setDisplayedText('');
    setIsAnimating(true);
    let index = 0;

    const intervalId = window.setInterval(() => {
      index = Math.min(index + charsPerTick, text.length);
      setDisplayedText(text.slice(0, index));

      if (index >= text.length) {
        window.clearInterval(intervalId);
        completedTextRef.current = text;
        setIsAnimating(false);
      }
    }, intervalMs);

    return () => window.clearInterval(intervalId);
  }, [text, enabled, charsPerTick, intervalMs]);

  const reset = useCallback(() => {
    completedTextRef.current = null;
    setDisplayedText('');
    setIsAnimating(false);
  }, []);

  return { displayedText, isAnimating, reset };
};

export default useWritingAnimation;
